import React from 'react';
import PropTypes from 'prop-types';
import history from 'State/history';
import fr from 'facet-rest';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';

export const SET_SELECTED_ID = 'set-selected-id';
export const SET_COLLECTION_ORDER = 'set-collection-order';
export const SET_COLLECTION = 'set-collection';
export const SET_SCHEMA = 'set-schema';
export const SET_ERROR = 'set-error';

const setCollection = (dispatch, collection) => {
  const currentSearchParams = new URLSearchParams(history.location.search);
  currentSearchParams.set('link', collection.link('self').url());
  history.push({
    pathname: history.location.pathname,
    search: currentSearchParams.toString(),
  });
  dispatch({ type: SET_COLLECTION, collection });
};

const AccordionListStateContext = React.createContext(undefined);
const AccordionListDispatchContext = React.createContext(undefined);


const defaultState = {
  order: { direction: 'asc', attrName: null },
  collection: null,
  schema: {},
  selectedId: null,
  error: null,
};

function init(initialAccordionList) {
  const {
    order, collection, schema, selectedId,
  } = {
    ...defaultState,
    ...initialAccordionList,
  };

  return {
    selectedId,
    order,
    collection,
    schema,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case SET_ERROR: {
      const { error } = action;
      return {
        ...state,
        error,
      };
    }
    case SET_COLLECTION_ORDER: {
      const { order } = action;
      return {
        ...state,
        order,
      };
    }
    case SET_COLLECTION: {
      const { collection } = action;
      return {
        ...state,
        collection,
      };
    }
    case SET_SCHEMA: {
      const { schema } = action;
      return {
        ...state,
        schema,
      };
    }
    case SET_SELECTED_ID: {
      const { id } = action;
      return {
        ...state,
        selectedId: id,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function urlsMatch(stringA, stringB) {
  if (!stringA || !stringB) {
    return false;
  }
  const urlA = new URL(stringA);
  const urlB = new URL(stringB);
  const aParams = urlA.searchParams;
  const bParams = urlB.searchParams;
  aParams.sort();
  bParams.sort();
  urlA.search = aParams.toString();
  urlB.search = bParams.toString();
  return urlA.toString() === urlB.toString();
}

function AccordionListProvider({
  children, schema, link: linkProp, defaultOrder,
}) {
  const [state, dispatch] = React.useReducer(
    reducer,
    { schema, order: defaultOrder },
    init,
  );
  const searchParams = new URLSearchParams(history.location.search);

  const link = searchParams.has('link') ? new fr.Link({ data: { name: 'self', url: searchParams.get('link') } }) : linkProp;

  React.useEffect(() => {
    async function fetchAndSetCollection() {
      const collection = await link.get();
      setCollection(dispatch, collection);
    }
    if (!urlsMatch(link.url(), state.collection?.link('self').url())) {
      fetchAndSetCollection().catch((e) => {
        console.error(e); // eslint-disable-line
        displayErrorMessage(e, 'Failed to load client experience households');
        dispatch({ type: SET_ERROR, error: e.message });
      });
    }
  }, [link.url()]);
  return (
    <AccordionListStateContext.Provider value={state}>
      <AccordionListDispatchContext.Provider value={dispatch}>
        {children}
      </AccordionListDispatchContext.Provider>
    </AccordionListStateContext.Provider>
  );
}

function useAccordionListStateContext() {
  const context = React.useContext(AccordionListStateContext);

  if (context === undefined) {
    throw new Error(
      'useAccordionListStateContext must be used within a AccordionListProvider',
    );
  }

  return context;
}

function useAccordionListDispatchContext() {
  const context = React.useContext(AccordionListDispatchContext);

  if (context === undefined) {
    throw new Error(
      'useAccordionListDispatchContext must be used within a AccordionListProvider',
    );
  }

  return context;
}

AccordionListProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  schema: PropTypes.object.isRequired,
  link: PropTypes.object.isRequired,
  defaultOrder: PropTypes.object.isRequired,
};

export {
  AccordionListProvider,
  useAccordionListStateContext,
  useAccordionListDispatchContext,
  setCollection,
};
